import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, ReactNode, useRef } from "react";
import tw from "twin.macro";

import { Button } from "./buttons";
import { Transition } from "./Transition";

interface ConfirmDialogProps {
  open: boolean;
  onConfirm: () => void;
  onClose: () => void;
  title: ReactNode;
  content: ReactNode;
  cancelText?: string;
  confirmText?: string;
}

const ConfirmDialog = ({
  open,
  onConfirm,
  onClose,
  title,
  content,
  cancelText,
  confirmText,
}: ConfirmDialogProps) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as="div"
        tw="relative z-10"
        onClose={onClose}
        initialFocus={cancelButtonRef}
      >
        <Transition.Child
          as={Fragment}
          enter={tw`ease-out duration-300`}
          enterFrom={tw`opacity-0`}
          enterTo={tw`opacity-100`}
          leave={tw`ease-in duration-200`}
          leaveFrom={tw`opacity-100`}
          leaveTo={tw`opacity-0`}
        >
          <div tw="fixed inset-0 bg-black bg-opacity-20 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div tw="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div tw="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter={tw`ease-out duration-300`}
              enterFrom={tw`opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95`}
              enterTo={tw`opacity-100 translate-y-0 sm:scale-100`}
              leave={tw`ease-in duration-200`}
              leaveFrom={tw`opacity-100 translate-y-0 sm:scale-100`}
              leaveTo={tw`opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95`}
            >
              <DialogPanel tw="flex flex-col gap-4 relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div tw="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    tw="rounded-md text-opacity-60 hover:text-opacity-100 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span tw="sr-only">Fermer</span>
                    <XMarkIcon tw="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div tw="sm:flex sm:items-start">
                  <div tw="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <DialogTitle as="h3" tw="text-base font-semibold leading-6">
                      {title}
                    </DialogTitle>
                    <div tw="mt-2 text-sm">{content}</div>
                  </div>
                </div>
                <div tw="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <Button
                    type="button"
                    red
                    tw="w-full sm:ml-3 sm:w-auto"
                    onClick={onConfirm}
                  >
                    {confirmText ?? "Confirmer"}
                  </Button>
                  <Button
                    type="button"
                    white
                    tw="mt-3 w-full sm:mt-0 sm:w-auto"
                    onClick={onClose}
                    ref={cancelButtonRef}
                  >
                    {cancelText ?? "Annuler"}
                  </Button>
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ConfirmDialog;
